import {
  Component,
  OnInit,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter,
} from "@angular/core";
import {
  faTimes,
  faSearch,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import { WatchlistPageService } from "../watchlist-page.service";
import { UntypedFormControl } from "@angular/forms";
import { debounceTime } from "rxjs/operators";
import {
  WatchlistSharingLog,
  UserSuggestions,
  UserSuggestionItem,
  WatchlistSharingLogItem,
} from "./share-watchlist.model";
import { AppService } from "src/app/app.service";

@Component({
  selector: "app-share-watchlist-modal",
  templateUrl: "./share-watchlist-modal.component.html",
  styleUrls: ["./share-watchlist-modal.component.scss"],
  encapsulation: ViewEncapsulation.None,
  //listen to keyboard events so we can trigger workflow for selected
  //email from the suggestion dropdown
  host: { "(document:keydown)": "handleKeyboardEvent($event)" },
})
export class ShareWatchlistModalComponent implements OnInit {
  @Input() visible: boolean;
  @Output() onClose: EventEmitter<boolean> = new EventEmitter();

  @Input() set watchlistId(watchlistId: number) {
    this._watchlistId = watchlistId;

    //apply default initial sort settings
    this.columnSortState = {
      recipient: null,
      email: null,
      date: "descend",
    };
    if (watchlistId !== -1) {
      this.fetchWatchlistSharingLog();
    }
  }

  emailInput: UntypedFormControl = new UntypedFormControl();

  faExclamationCircle = faExclamationCircle;
  _watchlistId: number;
  disableShareBtn = true;
  faCloseIcon = faTimes;
  faSearch = faSearch;
  allowUserSuggestionError = false;
  userSuggestions: UserSuggestionItem[] = [];
  sharedList: WatchlistSharingLogItem[] = [];
  sharedListUnmodified: WatchlistSharingLogItem[] = [];
  columnSortState: { [key: string]: "ascend" | "descend" | null } = {};

  constructor(
    private watchlistPageService: WatchlistPageService,
    private appService: AppService,
  ) {}

  ngOnInit() {
    //don't wait for debounce time.
    //instead make default state of share btn disabled, for every text change
    this.emailInput.valueChanges.subscribe(() => {
      this.disableShareBtn = true;
    });

    this.emailInput.valueChanges
      .pipe(debounceTime(500))
      .subscribe((text: string) => {
        //prevent error from displaying untill the userSuggestions have been fetched and populated
        this.allowUserSuggestionError = false;
        this.onTextChange(text);
      });
  }

  handleKeyboardEvent = (event: KeyboardEvent) => {
    if (!this.visible) {
      return;
    }

    if (event.keyCode === 13 && this.userSuggestions.length > 0) {
      //keycode 13 is for enter.
      //when the modal is visible and userSuggestions are more than 0
      //we can deduce that a item from dropdown was selected
      this.onSuggestionSelected();
    }
  };

  fetchWatchlistSharingLog = () => {
    this.watchlistPageService
      .getWatchlistSharingLog(this._watchlistId)
      .subscribe((data: WatchlistSharingLog) => {
        this.sharedList = data.data;
        this.sharedListUnmodified = [...this.sharedList];
        //sort data on defaut sorting priority -- sort by date -> sort alphabetically within date
        this.sortByReceiverName();
        this.sortByDate();
      });
  };

  closeModal = () => {
    //clear email input and suggestions
    this.emailInput.setValue("");
    this.userSuggestions = [];
    this.onClose.emit(true);
  };

  onSortChange = (value, key) => {
    this.columnSortState.recipient = null;
    this.columnSortState.email = null;
    this.columnSortState.date = null;
    this.columnSortState[key] = value;

    if (value === null) {
      this.sharedList = [...this.sharedListUnmodified];
      return;
    }

    switch (key) {
      case "recipient":
        this.sortByReceiverName();
        break;

      case "email":
        this.sortByEmail();
        break;

      case "date":
        this.sortByDate();
        break;

      default:
        this.sharedList = [...this.sharedListUnmodified];
    }
  };

  onTextChange = (text: string) => {
    if (text.trim() === "") {
      this.userSuggestions = []; //clear all cached/existing userSuggestions
      return; //return before suggestions from server
    }

    this.watchlistPageService
      .getUserSuggestions(encodeURIComponent(text))
      .subscribe((data: UserSuggestions) => {
        this.userSuggestions = data.data;
        //allow user suggestion error
        this.allowUserSuggestionError = true;
      });
  };

  shareWatchlist = (emailInputBox: HTMLInputElement) => {
    this.watchlistPageService
      .shareWatchlist(this._watchlistId, this.emailInput.value)
      .subscribe((res: WatchlistSharingLog) => {
        if (res.status.toLowerCase() === "success") {
          this.fetchWatchlistSharingLog();
        }
      });

    emailInputBox.focus();
    this.disableShareBtn = true;
    this.emailInput.setValue("");
  };

  onSuggestionSelected = () => {
    this.allowUserSuggestionError = false;
    this.userSuggestions = []; //clear userSuggestions -- stop showing dropdown
    this.disableShareBtn = false; //enable share button
  };

  sortByReceiverName = () => {
    const isDescend =
      this.columnSortState.recipient === "descend" ? true : false;
    const sortedList = [...this.sharedList];
    this.appService.basicSort(
      sortedList,
      ["recipient"],
      isDescend,
      (receiverName: string) => {
        return receiverName.toLowerCase();
      },
    );
    this.sharedList = sortedList;
  };

  sortByEmail = () => {
    const isDescend = this.columnSortState.email === "descend" ? true : false;
    const sortedList = [...this.sharedList];
    this.appService.basicSort(sortedList, ["email"], isDescend);
    this.sharedList = sortedList;
  };

  sortByDate = () => {
    const isDescend = this.columnSortState.date === "descend" ? true : false;
    const sortedList = [...this.sharedList];
    const transform = (date: string | null) => {
      if (date === null) {
        return Number.MAX_VALUE;
      }
      const dateToSort = date.split("-");
      return Number(`${dateToSort[2]}${dateToSort[0]}${dateToSort[1]}`);
    };
    this.appService.basicSort(sortedList, ["date"], isDescend, transform);
    this.sharedList = sortedList;
  };
}
